/* Theme-side cart checks. Checkout enforcement requires a Shopify validation app. */
(() => {
  const configNode = document.getElementById('CustomPreorderConfig');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const sectionId = 'custom-cart-preorder-state';
  let queue = Promise.resolve();
  const replay = new WeakSet();

  function failure(message, code, state) {
    return Object.assign(new Error(message), { code, preorderState: state });
  }
  const isGuardError = error => error?.code?.startsWith('CUSTOM_PREORDER_');
  const isMixed = lines => lines.some(line => line.quantity > 0 && line.preorder) &&
    lines.some(line => line.quantity > 0 && !line.preorder);

  function exclusive(task) {
    const pending = queue.then(task);
    queue = pending.catch(() => {});
    return pending;
  }

  async function readState(productUrl) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL(productUrl || config.cartUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid product origin');
      url.searchParams.set('sections', sectionId);
      // Keep section reads on this preview theme even without preview cookies.
      if (config.previewThemeId) url.searchParams.set('preview_theme_id', String(config.previewThemeId));
      url.searchParams.set('_preorder', String(Date.now()));
      const response = await fetch(url.toString(), { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Section request failed');
      const sections = await response.json();
      if (typeof sections[sectionId] !== 'string') throw new Error('Missing cart state section');
      const html = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      const node = html.querySelector('[data-custom-preorder-state]');
      if (!node) throw new Error('Missing cart state');
      const state = JSON.parse(node.textContent);
      if (!Array.isArray(state.lines) || !state.lines.every(line =>
        typeof line.key === 'string' && line.key && Number.isSafeInteger(line.id) &&
        Number.isInteger(line.quantity) && line.quantity > 0 && typeof line.preorder === 'boolean'
      )) throw new Error('Invalid cart lines');
      if (productUrl && (!state.product || typeof state.product.preorder !== 'boolean')) {
        throw new Error('Missing product status');
      }
      return state;
    } catch (error) {
      throw failure(config.retryMessage, 'CUSTOM_PREORDER_UNAVAILABLE');
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function check(state, operation) {
    const next = state.lines.map(line => ({ ...line }));
    let increases = false;
    if (operation.kind === 'add') {
      const quantity = Number(operation.quantity || 1);
      if (!state.product || !Number.isInteger(quantity) || quantity <= 0) {
        throw failure(config.retryMessage, 'CUSTOM_PREORDER_UNAVAILABLE');
      }
      next.push({ quantity, preorder: state.product.preorder });
      increases = true;
    } else if (operation.kind === 'update') {
      for (const [id, value] of Object.entries(operation.updates)) {
        const quantity = Number(value);
        if (!Number.isInteger(quantity) || quantity < 0) throw failure(config.retryMessage, 'CUSTOM_PREORDER_UNAVAILABLE');
        const line = next.find(item => item.key === id) || next.find(item => String(item.id) === id);
        increases ||= quantity > (line?.quantity || 0);
        if (line) line.quantity = quantity;
        else if (quantity > 0) {
          if (!state.product) throw failure(config.retryMessage, 'CUSTOM_PREORDER_UNAVAILABLE');
          next.push({ id: Number(id), quantity, preorder: state.product.preorder });
        }
      }
    } else if (operation.kind === 'change') {
      const line = next[Number(operation.line) - 1];
      const quantity = Number(operation.quantity);
      if (!line || !Number.isInteger(quantity) || quantity < 0) throw failure(config.retryMessage, 'CUSTOM_PREORDER_UNAVAILABLE');
      increases = quantity > line.quantity;
      line.quantity = quantity;
    }
    // Always permit quantity reductions/removals so an old mixed cart can be fixed.
    if (isMixed(next) && increases) throw failure(config.message, 'CUSTOM_PREORDER_MIXED', state);
    return next;
  }

  function showError(source, message) {
    if (source?.handleErrorMessage && source.dataset.hideErrors !== 'true' &&
      source.querySelector('.product-form__error-message-wrapper')) {
      source.handleErrorMessage(message);
      return;
    }
    const container = source || document.querySelector('main') || document.body;
    let error = container.querySelector('[data-preorder-form-error]');
    if (!error) {
      error = document.createElement('p');
      error.className = 'custom-preorder-error';
      error.dataset.preorderFormError = '';
      error.setAttribute('role', 'alert');
      container.appendChild(error);
    }
    error.textContent = message;
    error.hidden = false;
  }

  function restoreInputs(source, state) {
    if (!state) return;
    source.querySelectorAll('input[type="number"]').forEach(input => {
      const id = input.dataset.quantityVariantId || input.dataset.index;
      if (!id) return;
      input.value = state.lines.filter(line => String(line.id) === id).reduce((sum, line) => sum + line.quantity, 0);
    });
  }

  function applyState(state) {
    const mixed = isMixed(state.lines);
    document.querySelectorAll('[data-preorder-cart-warning]').forEach(node => { node.hidden = !mixed; });
    document.querySelectorAll('[data-preorder-checkout]').forEach(button => {
      if (mixed) {
        if (!button.disabled) button.dataset.preorderDisabled = 'true';
        button.disabled = true;
      } else if (button.dataset.preorderDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.preorderDisabled;
      }
    });
    document.querySelectorAll('[data-preorder-accelerated-cart]').forEach(node => { node.hidden = mixed; });
  }

  window.CustomPreorderGuard = {
    isGuardError, showError, restoreInputs,
    execute(operation, send) {
      return exclusive(async () => {
        // Removing lines must remain possible even if state verification is down.
        if ((operation.kind === 'change' && Number(operation.quantity) === 0) ||
          (operation.kind === 'update' && Object.values(operation.updates).every(value => Number(value) === 0))) {
          return send();
        }
        operation.source?.querySelectorAll('[data-preorder-form-error]').forEach(node => { node.hidden = true; });
        let state;
        try {
          state = await readState(operation.productUrl);
        } catch (error) {
          // Fail open only for verification failure. Never retry the mutation:
          // stock/network errors from send() must stay in Dawn's native flow.
          if (error.code !== 'CUSTOM_PREORDER_UNAVAILABLE') throw error;
          return send();
        }
        applyState(state);
        check(state, operation);
        return send();
      });
    }
  };

  // Check normal cart checkout again, including the notification's checkout form.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.submitter?.name !== 'checkout') return;
    if (replay.has(form)) { replay.delete(form); return; }
    if (new URL(form.action, window.location.origin).pathname !== new URL(config.cartUrl, window.location.origin).pathname) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter;
    if (button.dataset.preorderChecking) return;
    button.dataset.preorderChecking = 'true';
    button.setAttribute('aria-busy', 'true');
    exclusive(async () => {
      const state = await readState();
      applyState(state);
      if (isMixed(state.lines)) throw failure(config.message, 'CUSTOM_PREORDER_MIXED', state);
      if (!form.isConnected || !button.isConnected) return;
      replay.add(form);
      form.requestSubmit(button);
    }).catch(error => showError(form, error.message)).finally(() => {
      delete button.dataset.preorderChecking;
      button.removeAttribute('aria-busy');
    });
  }, true);

  let refreshTimer;
  if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => exclusive(async () => applyState(await readState())).catch(() => {}), 0);
  });
})();
